import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Package, MagnifyingGlass } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import LazyItemPhoto from "@/components/LazyItemPhoto";
import AddItemModal from "@/components/AddItemModal";
import { formatLocationPath } from "@/lib/locationPath";

export default function Items() {
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [locationId, setLocationId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [open, setOpen] = useState(false);

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (locationId) params.location_id = locationId;
    if (categoryId) params.category_id = categoryId;
    const [i, l, c] = await Promise.all([
      api.get("/items", { params }),
      api.get("/locations"),
      api.get("/categories"),
    ]);
    setItems(i.data); setLocations(l.data); setCategories(c.data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, locationId, categoryId]);

  return (
    <div className="space-y-8" data-testid="items-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl text-stoneink-900 font-semibold tracking-tight">Your things</h1>
          <p className="mt-2 text-stoneink-700">Everything you've nestled away.</p>
        </div>
        <button
          data-testid="add-item-btn"
          onClick={() => setOpen(true)}
          className="h-12 px-5 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium flex items-center gap-2 active:scale-[0.98] transition-all shadow-ambient"
        >
          <Plus weight="bold" /> New item
        </button>
      </div>

      <div className="rounded-3xl border border-sand-300 bg-white p-4 sm:p-5 shadow-ambient grid grid-cols-1 sm:grid-cols-[1fr_180px_180px] gap-3">
        <div className="relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stoneink-500" />
          <input
            data-testid="items-search-input"
            value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, notes, tag…"
            className="w-full h-11 pl-10 pr-3 rounded-xl bg-sand-100 border border-sand-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-500/20 outline-none"
          />
        </div>
        <select data-testid="items-filter-location" value={locationId} onChange={(e) => setLocationId(e.target.value)} className="h-11 px-3 rounded-xl bg-sand-100 border border-sand-300 outline-none">
          <option value="">All locations</option>
          {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <select data-testid="items-filter-category" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="h-11 px-3 rounded-xl bg-sand-100 border border-sand-300 outline-none">
          <option value="">All categories</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {items.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-sand-300 bg-sand-100 p-10 text-center">
          <Package weight="duotone" size={42} className="mx-auto text-stoneink-500" />
          <div className="mt-3 text-stoneink-700">No items match. Try adding a few.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((it, i) => {
            const path = formatLocationPath(locations, it.location_id);
            const cat = categories.find((c) => c.id === it.category_id);
            return (
              <motion.div key={it.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i, 12) * 0.03 }}>
                <Link
                  to={`/items/${it.id}`}
                  data-testid={`item-card-${it.id}`}
                  className="block rounded-3xl border border-sand-300 bg-white shadow-ambient hover:shadow-hover hover:-translate-y-0.5 transition-all overflow-hidden"
                >
                  <div className="aspect-[4/3] bg-sand-200">
                    {it.has_photo ? (
                      <LazyItemPhoto itemId={it.id} hasPhoto={it.has_photo} alt={it.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full grid place-items-center">
                        <Package weight="duotone" size={42} className="text-stoneink-500/60" />
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-heading text-lg text-stoneink-900 font-medium truncate">{it.name}</div>
                      {it.quantity > 1 && <span className="text-xs bg-sand-200 text-stoneink-700 rounded-full px-2 py-0.5 shrink-0">×{it.quantity}</span>}
                    </div>
                    <div className="text-sm text-stoneink-500 mt-1 truncate" title={path}>
                      {path ? `In ${path}` : "No location"} {cat ? `· ${cat.name}` : ""}
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      )}

      <AddItemModal open={open} onClose={() => setOpen(false)} onCreated={load} />
    </div>
  );
}
